'use client';

import Image from 'next/image';
import Link from 'next/link';
import { motion } from 'motion/react';
import { Zap, ArrowUpRight } from 'lucide-react';
import { ICONS } from '@/lib/iconRegistry';
import { Button } from '@/components/ui/button';
import GlobalTitle from '@shared/GlobalTitle';
import SectionShapes from '@shared/SectionShapes';
import CredentialsMarquee from '@shared/CredentialsMarquee';

/* ── animation variants ─────────────────────────────────────────── */
const fadeUp = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: 'easeOut' } },
};
const fadeRight = {
    hidden: { opacity: 0, x: -40 },
    visible: { opacity: 1, x: 0, transition: { duration: 0.6, ease: 'easeOut' } },
};
const fadeLeft = {
    hidden: { opacity: 0, x: 40 },
    visible: { opacity: 1, x: 0, transition: { duration: 0.6, ease: 'easeOut' } },
};
const stagger = {
    hidden: {},
    visible: { transition: { staggerChildren: 0.15 } },
};

/* ══════════════════════════════════════════════════════════════════
   Variant A
   Usage: <AboutServices data={aboutContentOnly} />
══════════════════════════════════════════════════════════════════ */
function ImageVariant({ data }) {
    return (
        <section className="relative bg-white py-12 md:py-20 overflow-hidden">
            <div className="absolute inset-0 bg-[radial-gradient(circle,_#e5e7eb_1px,_transparent_1px)] bg-[size:32px_32px] opacity-30 pointer-events-none" />
            <div className="absolute top-0 right-0 w-[600px] h-[450px] bg-primary/5 rounded-full blur-[130px] pointer-events-none" />
            <div className="relative z-10 max-w-[1440px] mx-auto px-6">
                <motion.div
                    variants={stagger}
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true, amount: 0.2 }}
                >
                    <motion.div variants={fadeUp}>
                        <h2 className="group font-serif font-black text-3xl leading-10 md:text-4xl md:leading-12 mb-10 text-left">
                            <span className="flex items-center gap-2 my-4 text-primary">
                                <svg width="24" height="6" viewBox="0 0 24 6" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M23.5 5C23.7761 5 24 5.22386 24 5.5C24 5.77614 23.7761 6 23.5 6L8.5 6C8.22386 6 8 5.77614 8 5.5C8 5.22386 8.22386 5 8.5 5L23.5 5Z"></path>
                                    <path d="M23.5 2.05443e-06C23.7761 2.07858e-06 24 0.22386 24 0.500002C24 0.776144 23.7761 1 23.5 1L0.5 1C0.223858 1 -6.78525e-08 0.776142 -4.37114e-08 0.5C-1.95703e-08 0.223858 0.223858 1.95703e-08 0.5 4.37114e-08L23.5 2.05443e-06Z"></path>
                                </svg>
                                <span className="block font-sans font-medium text-xs tracking-widest uppercase text-secondary group-[.text-white]:text-accent">
                                    {data.topText}
                                </span>
                            </span>
                            {data.titleText}
                            <em className="text-grad-red not-italic block">
                                {data.coloredText}
                            </em>
                        </h2>
                    </motion.div>
                </motion.div>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 xl:gap-20 items-start">
                    <motion.div
                        className="relative h-full"
                        variants={fadeRight}
                        initial="visible"
                        whileInView="visible"
                        viewport={{ once: true, amount: 0.2 }}
                    >
                        <span className="absolute -top-3 -left-3 w-10 h-10 border-t-2 border-l-2 border-primary/50 rounded-tl-xl z-10" />
                        <span className="absolute -bottom-3 -right-3 w-10 h-10 border-b-2 border-r-2 border-primary/50 rounded-br-xl z-10" />

                        <div className="relative w-full aspect-[4/3] rounded-2xl overflow-hidden shadow-xl shadow-gray-200 h-full">
                            <Image
                                src={data.image}
                                alt={data.imageAlt}
                                fill
                                sizes="(max-width: 1024px) 100vw, 50vw"
                                draggable="false"
                                quality={80}
                                loading="lazy"
                                className="object-cover object-center"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-gray-500/20 to-transparent" />
                        </div>

                        {data.badge && (
                            <motion.div
                                initial={{ opacity: 0, scale: 0.8 }}
                                whileInView={{ opacity: 1, scale: 1 }}
                                transition={{ delay: 0.45, duration: 0.5 }}
                                viewport={{ once: true }}
                                className="absolute -bottom-6 -left-0 sm:max-[1300px]:-left-2 min-[1300px]:-left-6 bg-primary text-white rounded-2xl px-6 py-5 shadow-xl shadow-primary/30 text-center"
                            >
                                <p className="text-4xl font-black leading-none">{data.badge.value}</p>
                                <p className="text-xs font-medium mt-1 opacity-90 tracking-wide">{data.badge.label}</p>
                            </motion.div>
                        )}
                    </motion.div>

                    <motion.div
                        variants={stagger}
                        initial="visible"
                        whileInView="visible"
                        viewport={{ once: true, amount: 0.2 }}
                        className="space-y-5"
                    >
                        {data.paragraphs?.map((para, i) => (
                            <motion.p key={i} variants={fadeUp} className="text-muted-foreground">
                                {para}
                            </motion.p>
                        ))}

                        {data.stats?.length > 0 && (
                            <motion.div variants={fadeUp} className="pt-2">
                                {data.dividerLabel && (
                                    <div className="flex items-center gap-2 sm:gap-3 mb-5">
                                        <span className="h-px flex-1 bg-gray-200" />
                                        <p className="text-[10px] sm:text-[xs] font-bold text-muted-foreground tracking-[0.06em] sm:tracking-[0.10em] uppercase text-center shrink-0">
                                            {data.dividerLabel}
                                        </p>
                                        <span className="h-px flex-1 bg-gray-200" />
                                    </div>
                                )}
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    {data.stats.map((stat) => {
                                        const Icon = ICONS[stat.icon];
                                        return (
                                            <div
                                                key={stat.label}
                                                className="group flex items-center gap-4 p-4 rounded-xl bg-[#f7f8fc] border border-gray-100 hover:border-primary/25 hover:bg-primary/5 transition-all duration-300"
                                            >
                                                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0 group-hover:bg-primary transition-colors duration-300">
                                                    {Icon && <Icon size={18} className="text-primary group-hover:text-white transition-colors duration-300" />}
                                                </div>
                                                <div>
                                                    <p className="text-2xl font-black text-gray-900 leading-none">
                                                        {stat.value}
                                                        <span className="text-primary">{stat.suffix}</span>
                                                    </p>
                                                    <p className="text-sm text-muted-foreground mt-0.5 leading-snug">{stat.label}</p>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </motion.div>
                        )}
                    </motion.div>
                </div>
            </div>
        </section>
    );
}

/* ══════════════════════════════════════════════════════════════════
   Variant B
   Usage: <AboutServices data={aboutContentDesign} />
══════════════════════════════════════════════════════════════════ */
function FeaturesVariant({ data }) {
    return (
        <section className="relative py-12 md:py-20 overflow-hidden">
            <SectionShapes variant="light" />
            <div className="absolute bottom-0 left-0 w-[500px] h-[400px] bg-primary/5 rounded-full blur-[120px] pointer-events-none" />
            <div className="relative z-10 max-w-[1440px] mx-auto px-6">
                <motion.div
                    variants={stagger}
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true, amount: 0.2 }}
                    className="mb-6"
                >
                    <motion.div variants={fadeUp}>
                        <GlobalTitle
                            topText={data.topText}
                            titleText={data.titleText}
                            coloredText={data.coloredText}
                            classes="text-left"
                        />
                    </motion.div>
                </motion.div>

                <div className="grid grid-cols-1 lg:grid-cols-[1fr_0.9fr] gap-12 xl:gap-20 items-start">
                    <motion.div
                        variants={stagger}
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true, amount: 0.2 }}
                        className="space-y-5"
                    >
                        {data.paragraphs?.map((para, i) => (
                            <motion.p key={i} variants={fadeUp} className="text-muted-foreground">
                                {para}
                            </motion.p>
                        ))}

                        {data.closingText && (
                            <motion.div
                                variants={fadeUp}
                                className="relative mt-2 rounded-2xl border-l-4 border-primary bg-white px-6 py-5 shadow-sm"
                            >
                                <span className="absolute right-1 top-1 text-gray-100">
                                    <Zap size={52} strokeWidth={1} />
                                </span>
                                <p className="font-semibold text-gray-900 pr-8">
                                    {data.closingText}
                                </p>
                            </motion.div>
                        )}

                        {data.stats?.length > 0 && (
                            <motion.div variants={fadeUp} className="pt-2">
                                {data.dividerLabel && (
                                    <div className="flex items-center gap-2 sm:gap-3 mb-5">
                                        <span className="h-px flex-1 bg-gray-200" />
                                        <p className="text-[10px] sm:text-[xs] font-bold text-muted-foreground tracking-[0.06em] sm:tracking-[0.10em] uppercase text-center shrink-0">
                                            {data.dividerLabel}
                                        </p>
                                        <span className="h-px flex-1 bg-gray-200" />
                                    </div>
                                )}
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    {data.stats.map((stat) => {
                                        const Icon = ICONS[stat.icon];
                                        return (
                                            <div
                                                key={stat.label}
                                                className="group flex items-center gap-4 p-4 rounded-xl bg-[#f7f8fc] border border-gray-100 hover:border-primary/25 hover:bg-primary/5 transition-all duration-300"
                                            >
                                                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0 group-hover:bg-primary transition-colors duration-300">
                                                    {Icon && <Icon size={18} className="text-primary group-hover:text-white transition-colors duration-300" />}
                                                </div>
                                                <div>
                                                    <p className="text-2xl font-black text-gray-900 leading-none">
                                                        {stat.value}
                                                        <span className="text-primary">{stat.suffix}</span>
                                                    </p>
                                                    <p className="text-sm text-muted-foreground mt-0.5 leading-snug">{stat.label}</p>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </motion.div>
                        )}

                        {(data.linkUrl || data.linkText) && (
                            <motion.div variants={fadeUp} className="pt-6">
                                <Link href={`${data.linkUrl ?? '/contact-us'}`}>
                                    <Button size="lg" className="bg-red-gradient border-0 text-sm md:text-base py-5 px-4 btn-glass group cursor-pointer">
                                        <span className="relative z-10 text-sm flex items-center gap-2 text-white group-hover:text-secondary transition-colors duration-400 leading-snug">
                                            {data.linkText}
                                            <ArrowUpRight size={16} className="transition-transform duration-200 group-hover:translate-x-1" />
                                        </span>
                                    </Button>
                                </Link>
                            </motion.div>
                        )}
                    </motion.div>

                    <motion.div
                        variants={stagger}
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true, amount: 0.2 }}
                    >
                        {data.featuresLabel && (
                            <motion.p variants={fadeUp} className="font-semibold text-lg text-gray-900 tracking-[0.02em] mb-2">
                                {data.featuresLabel}
                            </motion.p>
                        )}

                        {(Array.isArray(data.featuresContent)
                            ? data.featuresContent
                            : data.featuresContent
                                ? [data.featuresContent]
                                : []
                        ).map((para, i) => (
                            <motion.p
                                key={i}
                                variants={fadeUp}
                                className="text-muted-foreground mb-2"
                            >
                                {para}
                            </motion.p>
                        ))}

                        <div className={data.featuresContent?.length ? 'mt-5 space-y-3' : 'mt-0 space-y-3'}>
                            {data.features?.map((feature) => {
                                const Icon = ICONS[feature.icon];
                                return (
                                    <motion.div
                                        key={feature.label}
                                        variants={fadeLeft}
                                        className="group p-4 rounded-xl bg-white border border-gray-100 hover:border-primary/25 hover:shadow-md hover:shadow-primary/5 transition-all duration-300"
                                    >
                                        <div className='flex items-center gap-4'>
                                            <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0 group-hover:bg-primary transition-colors duration-300">
                                                {Icon && <Icon size={18} className="text-primary group-hover:text-white transition-colors duration-300" />}
                                            </div>
                                            <span className="font-semibold text-gray-800 text-sm group-hover:text-gray-900 transition-colors duration-200">
                                                {feature.label}
                                            </span>
                                        </div>

                                        {feature.labelpara && (
                                            <p className="text-sm text-muted-foreground mt-1">{feature.labelpara}</p>
                                        )}

                                        {feature.labelpoints?.length > 0 && (
                                            <ul className="mt-2 space-y-1.5">
                                                {feature.labelpoints.map((point) => (
                                                    <li key={point} className="flex items-start gap-2 text-sm text-muted-foreground">
                                                        <span className="mt-[7px] h-1.5 w-1.5 rounded-full bg-primary shrink-0" />
                                                        <span>{point}</span>
                                                    </li>
                                                ))}
                                            </ul>
                                        )}
                                    </motion.div>
                                );
                            })}
                        </div>

                        {data.buttonUrl && (
                            <motion.div variants={fadeUp} className="pt-6">
                                <Link href={`${data.buttonUrl ?? '/contact-us'}`}>
                                    <Button size="lg" className="bg-red-gradient border-0 text-sm md:text-base py-5 px-4 btn-glass group cursor-pointer">
                                        <span className="relative z-10 text-sm flex items-center gap-2 text-white group-hover:text-secondary transition-colors duration-400 leading-snug">
                                            {data.buttonText}
                                            <ArrowUpRight size={16} className="transition-transform duration-200 group-hover:translate-x-1" />
                                        </span>
                                    </Button>
                                </Link>
                            </motion.div>
                        )}

                        {data.showCredentials && (
                            <motion.div variants={fadeUp} className="pt-8">
                                <p className="text-xs font-semibold text-muted-foreground tracking-[0.15em] uppercase">Our Credentials</p>
                                <CredentialsMarquee className="bg-transparent pt-4" showMask={false} cardHeight="h-[60px]" />
                            </motion.div>
                        )}
                    </motion.div>
                </div>
            </div>
        </section>
    );
}

/* ══════════════════════════════════════════════════════════════════
   Examples:
     <AboutServices data={aboutContentOnly} />   → image variant
     <AboutServices data={aboutContentDesign} />        → features variant
══════════════════════════════════════════════════════════════════ */
export default function AboutServices({ data }) {
    if (!data) return null;
    return data.variant === 'features'
        ? <FeaturesVariant data={data} />
        : <ImageVariant data={data} />;
}
